import { useState, useContext, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
} from '@mui/material';
import { PlayArrow, PlayCircleOutline } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { CardSurface } from '../../ui/CardSurface';
import { ButtonPrimary } from '../../ui/ButtonPrimary';
import { BicoButton } from './BicoButton';
import { BicoDetalhesModal } from './BicoDetalhesModal';
import InstallPromptBar from './pwa-install/InstallPromptBar';
import { InstallPromptContext } from './pwa-install/InstallPromptContext';
import { obterUltimasAfericoesPorBico } from '../offline/api';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
};

export function HomeScreen({ empresa, afericaoAberta, onIniciarAfericao, onContinuarAfericao, loading }) {
  const temAfericaoAberta = !!afericaoAberta;
  const { showInstallUI } = useContext(InstallPromptContext);
  const [ultimasAfericoes, setUltimasAfericoes] = useState([]);
  const [bicos, setBicos] = useState([]);
  const [loadingBicos, setLoadingBicos] = useState(true);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [bicoParaDetalhes, setBicoParaDetalhes] = useState(null);

  useEffect(() => {
    if (empresa) {
      carregarUltimasAfericoes();
    }
  }, [empresa]);

  const carregarUltimasAfericoes = async () => {
    if (!empresa) return;
    
    setLoadingBicos(true);
    try {
      const empresaId = empresa.id || empresa.Id;
      const ultimasAfericoesData = await obterUltimasAfericoesPorBico(empresaId);
      setUltimasAfericoes(ultimasAfericoesData || []);
      
      // Mapear os bicos da empresa e combinar com as últimas aferições
      const bicosMapeados = mapBicosFromEmpresa(empresa, ultimasAfericoesData || []);
      setBicos(bicosMapeados);
    } catch (error) {
      console.error('Erro ao carregar últimas aferições:', error);
      // Em caso de erro, ainda mostrar os bicos da empresa sem aferições
      const bicosMapeados = mapBicosFromEmpresa(empresa, []);
      setBicos(bicosMapeados);
    } finally {
      setLoadingBicos(false);
    }
  };

  const mapBicosFromEmpresa = (empresa, ultimasAfericoes) => {
    const bicosList = [];
    const ultimasAfericoesMap = new Map();
    
    // Criar mapa das últimas aferições por número do bico
    ultimasAfericoes.forEach((afericao) => {
      const numeroBico = afericao.numero || afericao.Numero;
      if (numeroBico) {
        ultimasAfericoesMap.set(numeroBico, afericao);
      }
    });
    
    // Mapear bicos da empresa
    if (empresa.bombas && Array.isArray(empresa.bombas)) {
      empresa.bombas.forEach((bomba) => {
        if (bomba.bicos && Array.isArray(bomba.bicos)) {
          bomba.bicos.forEach((bico) => {
            const numeroBico = bico.numero || bico.Numero;
            const ultimaAfericao = ultimasAfericoesMap.get(numeroBico);
            
            bicosList.push({
              bico,
              realizado: !!ultimaAfericao,
              valor: ultimaAfericao?.valor ?? ultimaAfericao?.Valor ?? null,
              nivelAlerta: ultimaAfericao?.nivelAlerta ?? ultimaAfericao?.NivelAlerta ?? null,
              afericaoId: ultimaAfericao?.afericaoId ?? ultimaAfericao?.AfericaoId ?? null,
            });
          });
        }
      });
    } else if (empresa.bicos && Array.isArray(empresa.bicos)) {
      empresa.bicos.forEach((bico) => {
        const numeroBico = bico.numero || bico.Numero;
        const ultimaAfericao = ultimasAfericoesMap.get(numeroBico);
        
        bicosList.push({
          bico,
          realizado: !!ultimaAfericao,
          valor: ultimaAfericao?.valor ?? ultimaAfericao?.Valor ?? null,
          nivelAlerta: ultimaAfericao?.nivelAlerta ?? ultimaAfericao?.NivelAlerta ?? null,
          afericaoId: ultimaAfericao?.afericaoId ?? ultimaAfericao?.AfericaoId ?? null,
        });
      });
    } else if (empresa.Bicos && Array.isArray(empresa.Bicos)) {
      empresa.Bicos.forEach((bico) => {
        const numeroBico = bico.numero || bico.Numero;
        const ultimaAfericao = ultimasAfericoesMap.get(numeroBico);
        
        bicosList.push({
          bico,
          realizado: !!ultimaAfericao,
          valor: ultimaAfericao?.valor ?? ultimaAfericao?.Valor ?? null,
          nivelAlerta: ultimaAfericao?.nivelAlerta ?? ultimaAfericao?.NivelAlerta ?? null,
          afericaoId: ultimaAfericao?.afericaoId ?? ultimaAfericao?.AfericaoId ?? null,
        });
      });
    }

    // Ordenar por número do bico
    bicosList.sort((a, b) => {
      const numeroA = a.bico.numero || a.bico.Numero || a.bico.id || a.bico.Id || 0;
      const numeroB = b.bico.numero || b.bico.Numero || b.bico.id || b.bico.Id || 0;
      return numeroA - numeroB;
    });

    return bicosList;
  };

  const handleClick = () => {
    if (temAfericaoAberta) {
      onContinuarAfericao(afericaoAberta.id || afericaoAberta.Id);
    } else {
      onIniciarAfericao();
    }
  };

  const handleBicoClick = (bicoItem) => {
    // Se o bico tem última aferição, mostrar modal de detalhes
    if (bicoItem.realizado && bicoItem.afericaoId) {
      setBicoParaDetalhes(bicoItem);
      setShowDetalhes(true);
    }
  };

  return (
    <>
      <InstallPromptBar visible={true} />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          pt: showInstallUI ? '60px' : 0,
          transition: 'padding-top 0.3s ease-in-out',
          pb: { xs: 12, sm: 3 },
        }}
      >
        <Container maxWidth="lg" sx={{ flex: 1, py: 3, px: { xs: 2, sm: 3 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{ width: '100%', marginBottom: 24 }}
          >
            <Typography
              variant="h4"
              component="h1"
              fontWeight={700}
              gutterBottom
              sx={{ mb: 2, textAlign: 'center' }}
            >
              {empresa?.nome || empresa?.Nome || 'Aferição'}
            </Typography>
          </motion.div>

          {loadingBicos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: { xs: 1.5, sm: 2 },
                  width: '100%',
                }}
              >
                {bicos.map((bicoItem, index) => {
                  const bicoId = bicoItem.bico.id || bicoItem.bico.Id || index;
                  
                  return (
                    <motion.div
                      key={bicoId}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      style={{ width: '100%', minWidth: 0 }}
                    >
                      <BicoButton
                        bico={bicoItem.bico}
                        realizado={bicoItem.realizado}
                        valor={bicoItem.valor}
                        nivelAlerta={bicoItem.nivelAlerta}
                        onClick={() => handleBicoClick(bicoItem)}
                      />
                    </motion.div>
                  );
                })}
              </Box>
            </motion.div>
          )}
        </Container>

        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: { xs: 2, sm: 3 },
            bgcolor: 'background.paper',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: 1000,
          }}
        >
          <Container maxWidth="lg">
            <ButtonPrimary
              size="large"
              onClick={handleClick}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : temAfericaoAberta ? (
                  <PlayCircleOutline />
                ) : (
                  <PlayArrow />
                )
              }
              sx={{
                width: '100%',
                py: 2,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {loading
                ? 'Processando...'
                : temAfericaoAberta
                ? 'Continuar Aferição'
                : 'Nova Aferição'}
            </ButtonPrimary>
          </Container>
        </Box>
      </Box>

      {showDetalhes && bicoParaDetalhes && bicoParaDetalhes.afericaoId && (
        <BicoDetalhesModal
          open={showDetalhes}
          onClose={() => {
            setShowDetalhes(false);
            setBicoParaDetalhes(null);
          }}
          afericaoId={bicoParaDetalhes.afericaoId}
          numeroBico={bicoParaDetalhes.bico.numero || bicoParaDetalhes.bico.Numero || bicoParaDetalhes.bico.id || bicoParaDetalhes.bico.Id}
          onDeleted={async () => {
            // Recarregar as últimas aferições após deletar
            await carregarUltimasAfericoes();
          }}
          afericaoFinalizada={true}
        />
      )}
    </>
  );
}
